<template>
  <div class="panel cc-panel">
    <p class="panel-heading">
      {{ $t("i18n.chartTitles.ccCOPPerTemperature") }}
    </p>
    <div class="panel-block">
      <div :id="this.idChartDiv" class="cc-chart"></div>
    </div>
  </div>
</template>
<script>
import * as am5 from '@amcharts/amcharts5'
import * as am5xy from '@amcharts/amcharts5/xy'
import * as am5exporting from '@amcharts/amcharts5/plugins/exporting'
import ccColors from '@/components/ccColors'

import { System } from '@/classes/System'

export default {
  name: 'ccCOPPerTemperature',
  props: {
    idChartDiv: {
      type: String,
      required: true
    },
    xAxisDataField: {
      type: String,
      required: true
    },
    data: {
      type: Array,
      required: true
    },
    system: {
      type: System,
      required: true
    }
  },
  data () {
    return {
      copPerApplicationType: []
    }
  },
  mounted () {
    this.buildChart()
  },
  methods: {
    buildChart: function () {
      const systemApplicationTypes = []

      this.data.forEach(dataPoint => {
        // check if there allready is a datapoint prepared for the chart for this temperature
        const existingObjectForThisTemperature = this.copPerApplicationType.find(
          aggregatedTemperatureObject => aggregatedTemperatureObject.outdoorTemperatureRounded === dataPoint.outdoorTemperatureRounded
        )

        // Get the application type for this datapoint
        const dataPointApplicationType = this.system.units.find(unit => unit.id === dataPoint.idUnit)?.application

        // add dataPointApplicationType so lit of system applicationtypes.
        if (!systemApplicationTypes.includes(dataPointApplicationType)) {
          systemApplicationTypes.push(dataPointApplicationType)
        }

        // Temperature not yet existing, add it
        if (!existingObjectForThisTemperature) {
          // Create temperature objec
          const temperatureObject = {
            outdoorTemperatureRounded: dataPoint.outdoorTemperatureRounded
          }
          temperatureObject[dataPointApplicationType + '_Cooling'] = dataPoint.energyCooling
          temperatureObject[dataPointApplicationType + '_Heating'] = dataPoint.energyHeating
          temperatureObject[dataPointApplicationType + '_TotalEnergy'] = dataPoint.energyTot
          this.copPerApplicationType.push(temperatureObject)
        } else {
          // check if the temperature object allready has this application type?
          // append to existing temperature object
          if (existingObjectForThisTemperature[dataPointApplicationType + '_Cooling']) {
            existingObjectForThisTemperature[dataPointApplicationType + '_Cooling'] += dataPoint.energyCooling
          } else {
            existingObjectForThisTemperature[dataPointApplicationType + '_Cooling'] = dataPoint.energyCooling
          }
          if (existingObjectForThisTemperature[dataPointApplicationType + '_Heating']) {
            existingObjectForThisTemperature[dataPointApplicationType + '_Heating'] += dataPoint.energyHeating
          } else {
            existingObjectForThisTemperature[dataPointApplicationType + '_Heating'] = dataPoint.energyHeating
          }
          if (existingObjectForThisTemperature[dataPointApplicationType + '_TotalEnergy']) {
            existingObjectForThisTemperature[dataPointApplicationType + '_TotalEnergy'] += dataPoint.energyTot
          } else {
            existingObjectForThisTemperature[dataPointApplicationType + '_TotalEnergy'] = dataPoint.energyTot
          }
        }
      })

      this.copPerApplicationType.sort((a, b) => {
        return a.outdoorTemperatureRounded - b.outdoorTemperatureRounded
      })

      // per temperature and application, Calculate totalCOP
      this.copPerApplicationType.forEach(temperatureObject => {
        systemApplicationTypes.forEach(applicationType => {
          temperatureObject[applicationType + '_CoolingCOP'] =
          parseFloat((temperatureObject[applicationType + '_Cooling'] /
          temperatureObject[applicationType + '_TotalEnergy']).toFixed(2))

          temperatureObject[applicationType + '_HeatingCOP'] =
          parseFloat((temperatureObject[applicationType + '_Heating'] /
          temperatureObject[applicationType + '_TotalEnergy']).toFixed(2))
        })
      })

      am5.addLicense(this.$store.state.am5)
      const root = am5.Root.new(this.idChartDiv)

      // Create chart
      // https://www.amcharts.com/docs/v5/charts/xy-chart/
      const chart = root.container.children.push(am5xy.XYChart.new(root, {
        layout: root.verticalLayout,
        connect: false,
        paddingRight: 40,
        paddingTop: 0
      }))

      // Create axes
      const xAxis = chart.xAxes.push(am5xy.CategoryAxis.new(root, {
        categoryField: this.xAxisDataField,
        renderer: am5xy.AxisRendererX.new(root, {
          minGridDistance: 15
        }),
        tooltip: am5.Tooltip.new(root, {})
      }))

      xAxis.data.setAll(this.copPerApplicationType)

      const yAxis = chart.yAxes.push(am5xy.ValueAxis.new(root, {
        renderer: am5xy.AxisRendererY.new(root, {}),
        min: 0
      }))

      // Add line series
      systemApplicationTypes.forEach((applicationType, index) => {
        let title = this.$t('i18n.unitApplications.' + applicationType)
        if (applicationType === 'UKN') {
          title += ' ' + this.$t('i18n.systemList.header')
        }

        const coolingTitle = title + ' ' + this.$t('i18n.global.totalCoolingCop')
        const heatingTitle = title + ' ' + this.$t('i18n.global.totalHeatingCop')

        this.addSmoothedLineSeries(coolingTitle, applicationType + '_CoolingCOP', ccColors.cold[index], root, chart, yAxis, xAxis)
        this.addSmoothedLineSeries(heatingTitle, applicationType + '_HeatingCOP', ccColors.warm[index], root, chart, yAxis, xAxis)
      })

      // Add legend
      const legend = chart.children.push(am5.Legend.new(root, {
        centerX: am5.p50,
        x: am5.p50,
        clickTarget: 'none' // Disable clickable legends
      }))

      legend.data.setAll(chart.series.values)

      // Add export menu
      am5exporting.Exporting.new(root, {
        menu: am5exporting.ExportingMenu.new(root, {})
      })

      chart.children.unshift(am5.Label.new(root, {
        text: 'COP',
        fontWeight: '500',
        textAlign: 'center',
        y: am5.percent(-10),
        height: 0,
        paddingTop: 30
      }))

      chart.children.unshift(am5.Label.new(root, {
        text: '°C',
        fontWeight: '500',
        textAlign: 'left',
        x: am5.percent(100),
        y: am5.percent(81),
        paddingLeft: 10
      }))

      // Make stuff animate on load
      chart.appear(1000, 100)
    },
    addSmoothedLineSeries: function (title, value, color, root, chart, yAxis, xAxis) {
      // add axis for this line series

      const lineSeries = chart.series.push(
        am5xy.SmoothedXLineSeries.new(root, {
          name: title,
          xAxis: xAxis,
          yAxis: yAxis,
          valueYField: value,
          categoryXField: this.xAxisDataField,
          stroke: color,
          fill: color,
          connect: false
        })
      )

      // Add debugger tooltip
      const tooltip = lineSeries.set('tooltip', am5.Tooltip.new(root, {
        pointerOrientation: 'horizontal'
      }))

      tooltip.label.setAll({
        text: '{name}: {valueY}'
      })

      lineSeries.strokes.template.setAll({
        strokeWidth: 2
      })

      lineSeries.data.setAll(this.copPerApplicationType)

      return lineSeries
    }
  }
}
</script>
